import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Table,
  Pagination,
  Card,
  Row,
  Col,
  Container,
  Alert,
  Button,
  Modal,
  Form,
  Spinner,
} from "react-bootstrap";
import "../App.css";
import { FaHome } from "react-icons/fa";

function Question() {
  const location = useLocation();
  const { assessmentId, assessmentName } = location.state || {};
  const navigate = useNavigate();
  const headers = ["Type", "Questions", "Tags", "Points"];
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 20;
  const [showModal, setShowModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [formData, setFormData] = useState({
    question_text: "",
    right_feedback: "That's Correct.",
    wrong_feedback: "Sorry! Try again.",
    options: [
      { answer_text: "", is_correct: false },
      { answer_text: "", is_correct: false },
    ],
  });
  const [loading, setLoading] = useState(false);

  const handleHomeClick = () => {
    navigate(-1);
  };

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/getQuestions?assessment_id=${assessmentId}`
      );
      const data = await response.json();

      if (data.success) {
        setRows(data.data);
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error("Error fetching question data:", error);
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    if (assessmentId) {
      fetchQuestions();
    }
  }, [assessmentId, fetchQuestions]);

  const handlePagination = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = rows.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(rows.length / rowsPerPage);

  const handleRowClick = async (question) => {
    setSelectedQuestion(question);

    const fetchAnswersForQuestion = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API}/getAnswersByQuestionId/${question.question_id}`
        );
        const data = await response.json();
        if (data.success) {
          return data.answers;
        } else {
          console.error(data.message);
          return [];
        }
      } catch (error) {
        console.error("Error fetching answers:", error);
        return [];
      }
    };

    const answers = await fetchAnswersForQuestion();

    const updatedOptions = answers.map((answer) => ({
      answer_id: answer.answer_id || null,
      answer_text: answer.answer_text || "",
      is_correct: answer.is_correct || false,
    }));

    setFormData({
      question_text: question.question_text || "",
      right_feedback: question.right_feedback || "That's Correct.",
      wrong_feedback: question.wrong_feedback || "Sorry! Try again.",
      options:
        updatedOptions.length > 0
          ? updatedOptions
          : [
              { answer_id: null, answer_text: "", is_correct: false },
              { answer_id: null, answer_text: "", is_correct: false },
            ],
    });

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedQuestion(null);
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/updateQuestionAndAnswers`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question_id: selectedQuestion.question_id,
            ...formData,
          }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.question_id === selectedQuestion.question_id
              ? { ...row, question_text: formData.question_text }
              : row
          )
        );
        fetchQuestions();
        setShowModal(false);
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error("Error saving changes:", error.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (index) => {
    const updatedOptions = formData.options.map((option, i) => ({
      ...option,
      is_correct: i === index ? !option.is_correct : option.is_correct,
    }));
    setFormData({ ...formData, options: updatedOptions });
  };

  const handleDeleteQuestion = async () => {
    if (!selectedQuestion || !selectedQuestion.question_id) return;

    if (
      !window.confirm(
        "Are you sure you want to delete this question?\n\nNoted: If you deleted the question and answers, It will affect on the submitted assessment from the learner."
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/deleteQuestionAnswers/${selectedQuestion.question_id}`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json();
      if (data.success) {
        alert("Question and answers deleted successfully.");
        setRows((prevRows) =>
          prevRows.filter(
            (row) => row.question_id !== selectedQuestion.question_id
          )
        );
        setShowModal(false);
        setSelectedQuestion(null);
      } else {
        console.error(data.message);
        alert("Failed to delete question. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting question:", error);
      alert("An error occurred. Please try again.");
    }
  };

  const background = {
    backgroundColor: "#263F86",
    borderRadius: "10px",
    fontFamily: "Manrope-Regular",
  };

  const renderPaginationItems = () => {
    const maxVisiblePages = 5; // Number of page numbers to show
    const pages = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Pagination.Item key={1} onClick={() => handlePagination(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        pages.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePagination(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      pages.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePagination(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return pages;
  };

  return (
    <Container>
      {loading ? (
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ height: "80vh" }}
        >
          <Spinner animation="border" variant="primary" />
          <p className="mt-3" style={{ fontFamily: "Manrope-Regular" }}>
            Please Wait...
          </p>
        </div>
      ) : (
        <>
          <div className="row">
            <div className="col-md-12">
              <h3
                className="text-start p-2 d-flex justify-content-between align-items-center"
                style={{ color: "#263F86", fontFamily: "Manrope-Regular" }}
              >
                {assessmentName || "Assessment"}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleHomeClick}
                  style={{ fontSize: "0.9rem" }}
                >
                  <FaHome
                    style={{
                      marginRight: "5px",
                      fontFamily: "Manrope-Regular",
                    }}
                  />{" "}
                  Home
                </Button>
              </h3>
            </div>
          </div>

          <hr />
          <Row className="mb-4 gx-6">
            <Col
              xs={12}
              md={4}
              className="mb-3 mb-md-0 d-flex justify-content-start"
            >
              <Card className="text-center w-100" style={background}>
                <Card.Body>
                  <Card.Title className="text-white">Questions</Card.Title>
                  <Card.Text
                    className="text-white"
                    style={{ fontSize: "25px" }}
                  >
                    {rows.length}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}></Col>
            <Col xs={12} md={4} className="d-flex justify-content-end">
              <Card className="text-center w-100" style={background}>
                <Card.Body>
                  <Card.Title className="text-white">Points</Card.Title>
                  <Card.Text
                    className="text-white"
                    style={{ fontSize: "25px" }}
                  >
                    {rows.reduce((total, row) => total + (row.point || 1), 0)}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            {rows.length === 0 ? (
              <Alert
                variant="info"
                className="text-center w-100"
                style={{ fontFamily: "Manrope-Regular" }}
              >
                No questions available for this assessment.
              </Alert>
            ) : (
              <>
                <Table
                  striped
                  bordered
                  hover
                  responsive
                  style={{ fontFamily: "Manrope-Regular" }}
                >
                  <thead>
                    <tr style={{ color: "#263F86" }}>
                      {headers.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((row, index) => (
                      <tr
                        key={index}
                        style={{ color: "black", cursor: "pointer" }}
                        onClick={() => handleRowClick(row)}
                      >
                        <td>{row.type_name || "MCQ"}</td>
                        <td className="text-start">
                          <div>
                            <div
                              style={{ fontSize: "16px", marginBottom: "5px" }}
                            >
                              {row.question_text}
                            </div>
                            <div
                              className="row"
                              style={{
                                color: "#fff",
                                fontSize: "12px",
                                marginTop: "10px",
                              }}
                            >
                              <span
                                className="col mx-3 d-flex align-items-center "
                                style={{
                                  backgroundColor: "#1F6690",
                                  borderRadius: "3px",
                                }}
                              >
                                <b>
                                  {row.correct_answer
                                    ? row.correct_answer.length > 40
                                      ? `${row.correct_answer.substring(
                                          0,
                                          40
                                        )}...`
                                      : row.correct_answer
                                    : "N/A"}
                                </b>
                              </span>
                              <span className="col mt-1 mx-1">
                                <b
                                  className="p-1"
                                  style={{
                                    backgroundColor: "#5d6d7e",
                                    borderRadius: "3px",
                                  }}
                                >
                                  {row.option_count || 0} Options
                                </b>
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>{row.tags_name || "Old History"}</td>
                        <td>{row.score || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <div className="d-flex justify-content-center mt-2">
                  <Pagination style={{ fontFamily: "Manrope-Regular" }}>
                    <Pagination.Prev
                      onClick={() => handlePagination(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Pagination.Prev>

                    {renderPaginationItems()}

                    <Pagination.Next
                      onClick={() => handlePagination(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Pagination.Next>
                  </Pagination>
                </div>
              </>
            )}
          </Row>
        </>
      )}

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        style={{ fontFamily: "Manrope-Regular" }}
      >
        <form onSubmit={handleSaveChanges}>
          <Modal.Header closeButton>
            <div className="d-flex w-100 justify-content-between align-items-center">
              <Modal.Title style={{ color: "#263F86" }}>
                {assessmentName || "Module Questions"}
              </Modal.Title>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="row p-2">
              <div className="col-md-12">
                <textarea
                  className="form-control"
                  name="question_text"
                  value={formData.question_text}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Enter your question here"
                ></textarea>
              </div>
            </div>

            <h5 className="fw-bold mt-4" style={{ color: "#263F86" }}>
              Answers
            </h5>
            {formData.options.map((option, index) => (
              <div className="container mb-3" key={index}>
                <div className="row align-items-center">
                  <div className="col-auto">
                    <Form.Check
                      type="checkbox"
                      label=""
                      className="custom-checkbox"
                      checked={option.is_correct}
                      onChange={() => handleCheckboxChange(index)}
                    />
                  </div>
                  <div className="col">
                    <Form.Control
                      type="text"
                      placeholder={`Option ${index + 1}`}
                      value={option.answer_text}
                      onChange={(e) => {
                        const updatedOptions = [...formData.options];
                        updatedOptions[index].answer_text = e.target.value;
                        setFormData({ ...formData, options: updatedOptions });
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}

            <h5 className="mt-4 fw-bold" style={{ color: "#263F86" }}>
              Feedback
            </h5>
            <div className="row">
              <div className="col">
                <p>Correct Feedback</p>
                <textarea
                  className="form-control"
                  name="right_feedback"
                  value={formData.right_feedback}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Enter feedback for correct answers"
                ></textarea>
              </div>
              <div className="col">
                <p>Wrong Feedback</p>
                <textarea
                  className="form-control"
                  name="wrong_feedback"
                  value={formData.wrong_feedback}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Enter feedback for incorrect answers"
                ></textarea>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <Button
              type="button"
              className="btn btn-danger"
              onClick={handleDeleteQuestion}
            >
              Delete
            </Button>
            <Button
              type="submit"
              className="btn btn-primary"
              style={{ backgroundColor: "#263F86" }}
            >
              Save Changes
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </Container>
  );
}

export default Question;
